import React, { useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

const buildPageRange = (currentPage, lastPage) => {
  const current = Number(currentPage) || 1;
  const last = Number(lastPage) || 1;

  if (last <= 7) {
    return Array.from({ length: last }, (_, index) => index + 1);
  }

  const pages = [1];

  if (current > 4) pages.push("left-ellipsis");

  for (
    let page = Math.max(2, current - 1);
    page <= Math.min(last - 1, current + 1);
    page += 1
  ) {
    pages.push(page);
  }

  if (current < last - 3) pages.push("right-ellipsis");
  pages.push(last);

  return pages;
};

function ContactsPagination({
  currentPage = 1,
  lastPage = 1,
  from = 0,
  to = 0,
  total = 0,
  loading = false,
  onPageChange,
}) {
  const current = Number(currentPage) || 1;
  const last = Number(lastPage) || 1;
  const totalRecords = Number(total) || 0;
  const rangeFrom = Number(from) || 0;
  const rangeTo = Number(to) || 0;
  const hasPrevious = current > 1;
  const hasNext = current < last;

  const pageRange = useMemo(
    () => buildPageRange(current, last),
    [current, last],
  );

  const changePage = (page) => {
    const targetPage = Number(page);

    if (
      loading
      || !Number.isInteger(targetPage)
      || targetPage < 1
      || targetPage > last
      || targetPage === current
    ) {
      return;
    }

    onPageChange?.(targetPage);
  };

  if (!loading && totalRecords === 0) return null;

  const navigationButton =
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#e1eaef] bg-white text-[#5b7180] transition-all hover:border-[#c7dce5] hover:bg-[#eaf4fa] hover:text-[#147db5] disabled:cursor-not-allowed disabled:border-transparent disabled:bg-[#f6f9fb] disabled:text-[#c7dce5] dark:border-[#354d44] dark:bg-[#182621] dark:text-[#9bb2a9] dark:disabled:bg-[#14201c] dark:disabled:text-[#485c54]";

  return (
    <footer className="flex flex-col gap-3 border-t border-[#e1eaef] bg-[#f6f9fb] px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-6 dark:border-[#2b4039] dark:bg-[#17231f]">
      <div className="flex items-center justify-between gap-3 sm:block">
        <p className="text-[11px] font-bold text-[#34506a] dark:text-[#b9ccc4]">
          {rangeFrom}–{rangeTo}
          <span className="font-medium text-[#8a9ba8]"> of </span>
          {totalRecords}
        </p>
        <p className="mt-0.5 text-[9px] font-medium text-[#8a9ba8] dark:text-[#748980]">
          Contacts in your directory
        </p>
      </div>

      <nav className="flex items-center justify-between gap-1.5 sm:justify-end" aria-label="Contacts pagination">
        <button
          type="button"
          disabled={!hasPrevious || loading}
          onClick={() => changePage(1)}
          className={`${navigationButton} hidden sm:flex`}
          aria-label="First page"
        >
          <ChevronsLeft size={14} />
        </button>
        <button
          type="button"
          disabled={!hasPrevious || loading}
          onClick={() => changePage(current - 1)}
          className={navigationButton}
          aria-label="Previous page"
        >
          <ChevronLeft size={15} />
        </button>

        <div className="flex items-center gap-1">
          {pageRange.map((page, index) => {
            if (typeof page !== "number") {
              return (
                <span
                  key={`${page}-${index}`}
                  className="flex h-8 w-5 items-center justify-center text-[10px] font-bold text-[#9cafb8]"
                >
                  ···
                </span>
              );
            }

            const active = page === current;

            return (
              <button
                key={page}
                type="button"
                disabled={loading}
                onClick={() => changePage(page)}
                aria-current={active ? "page" : undefined}
                aria-label={`Page ${page}`}
                className={`flex h-8 min-w-[32px] items-center justify-center rounded-full px-2 text-[10px] font-bold transition-all ${active ? "bg-[#0f6392] text-white shadow-[0_6px_16px_rgba(47,114,93,0.18)]" : "text-[#5b7180] hover:bg-[#eaf4fa] hover:text-[#147db5] dark:text-[#90a69d] dark:hover:bg-[#21362e]"}`}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          disabled={!hasNext || loading}
          onClick={() => changePage(current + 1)}
          className={navigationButton}
          aria-label="Next page"
        >
          <ChevronRight size={15} />
        </button>
        <button
          type="button"
          disabled={!hasNext || loading}
          onClick={() => changePage(last)}
          className={`${navigationButton} hidden sm:flex`}
          aria-label="Last page"
        >
          <ChevronsRight size={14} />
        </button>
      </nav>
    </footer>
  );
}

export default ContactsPagination;
